<template>
  <component
    :is="component"
    type="button"
    :href="href"
    :to="to"
    :target="target"
    class="group rounded-full cursor-pointer flex-col items-center leading-none transition active:translate-y-0.5 md:hover:-translate-y-0.5 disabled:opacity-50 disabled:pointer-events-none relative appearance-none active:shadow-sm shadow md:hover:shadow-md justify-center font-medium"
    :class="[
      getStyle(styles, 'container'),
      isDisabled && (to || href) ? '!pointer-events-none' : ''
    ]"
    :style="{
      transformStyle: 'preserve-3d',
      backfaceVisibility: 'hidden',
    }"
    :disabled="isDisabled"
    draggable="false"
    v-bind="attrs"
    @click="() => isVibrate ? vibrate(ImpactStyle.Light) : true"
  >
    <div class="z-[60] transition pointer-events-none leading-tight absolute -top-2 text-white" :class="tooltip ? 'animate-bounce-2 max-w-28' : 'opacity-0 translate-y-3 w-0 h-0'" v-if="tooltip">
      <div class="-translate-y-full text-center" style="font-size: 0;">
        <div class="bg-charcoal-950/90 dark:bg-dark-500/80 dark:text-dark-50 p-1.5 rounded-md font-medium text-3xs">
          {{ tooltip }}
        </div>
        <div class="inline-block border-t-[6px] border-t-charcoal-950/90 dark:border-t-dark-500/80" style="{ width: 0; height: 0; border-right: 6px solid transparent; border-left: 6px solid transparent; }" />
      </div>
    </div>

    <div class="z-[60] transition pointer-events-none leading-tight absolute -bottom-2 text-white" :class="tooltipBottom ? 'animate-bounce-2-reverse max-w-28' : 'opacity-0 translate-y-3 w-0 h-0'" v-if="tooltipBottom">
      <div class="translate-y-full text-center" style="font-size: 0;">
        <div class="inline-block border-b-[6px] border-b-charcoal-950/90 dark:border-b-dark-500/80" style="{ width: 0; height: 0; border-right: 6px solid transparent; border-left: 6px solid transparent; }" />
        <div class="bg-charcoal-950/90 dark:bg-dark-500/80 dark:text-dark-50 p-1.5 rounded-md font-medium text-3xs">
          {{ tooltipBottom }}
        </div>
      </div>
    </div>
    
    <img
      :src="gradient"
      class="absolute w-full h-full top-0 pointer-events-none rounded-full md:group-md:hover:opacity-60 dark:opacity-50 opacity-100 transition md:group-md:hover:translate-y-0.5 duration-700 select-none"
      :style="{ transformStyle: 'preserve-3d', backfaceVisibility: 'hidden' }"
      draggable="false" :class="[ getStyle(styles, 'blur') ]"
      v-if="gradient && isBlur"
    >

    <div :style="{ backgroundImage: `url(${gradient})` }" class="absolute w-full h-full top-0 rounded-full bg-cover bg-center pointer-events-none" v-if="gradient" />

    <div class="flex items-center group-active:opacity-60 group-active:scale-[0.99] align-middle text-center relative z-10" :class="getStyle(styles, 'content')">
      <IconBase :name="icon" class="mx-1" :width="iconSize" :class="iconClass" v-if="icon" />

      <span class="line-clamp-1 break-all" v-if="isClamped">
        <slot />
      </span>
      <slot v-else />
      
      <span class="w-6 h-6 bg-white rounded-full text-light-950 text-xs font-bold inline-flex items-center justify-center mx-1.5" v-if="count !== false">
        <p>{{ count }}</p>
      </span>
    </div>

    <div class="z-10 relative opacity-60 mt-0.5" :class="[ getStyle(styles, 'subtitle'), getStyle(styles, 'content') ]" v-if="$slots.subtitle">
      <slot name="subtitle" />
    </div>

    <div class="absolute w-full left-0 top-1/2 -translate-y-1/2 text-center" v-show="isLoading">
      <IconBase class="spin !inline-block mx-auto" name="cute/spinner" />
    </div>

    <div class="absolute w-full text-white left-0 top-1/2 -translate-y-1/2 pointer-events-none text-center z-10" v-show="isWaiting">
      <p class="w-6 h-6 bg-charcoal-950 rounded-full inline-flex items-center justify-center text-2xs">{{ timeLeft }}</p>
    </div>
  </component>
</template>

<script lang="ts" setup>
import { defineStyles, getStyle } from '@/utils/useStyles'
import gradientBase from '@/assets/img/gradients/hard/gradient-2.jpg'

import gradientSupporter from '@/assets/img/gradients/hard/gradient-11.jpg'
import gradientBuilders from '@/assets/img/gradients/hard/gradient-3.jpg'
import gradientMember from '@/assets/img/gradients/hard/gradient-7.jpg'
import gradientFounder from '@/assets/img/gradients/hard/gradient-9.jpg'
import gradientIntentions from '@/assets/img/gradients/hard/gradient-12.jpg'
import gradientGold from '@/assets/img/gradients/hard/gradient-13.jpg'
import gradientSpicy from '@/assets/img/gradients/hard/gradient-14.jpg'
import { useKeepAlive } from '@/utils/useKeepAlive'
import { ImpactStyle } from '@capacitor/haptics'

const props = withDefaults(defineProps<{
  href?: string
  to?: RouteLocationRaw
  isVibrate?: boolean
  count?: number | boolean
  target?: string
  tooltip?: string
  tooltipBottom?: string
  size?: string
  color?: keyof typeof BUTTON_COLORS
  contentAlign?: string
  styleType?: 'base' | 'round'
  isDisabled?: boolean
  isFull?: boolean
  isClamped?: boolean
  isBlur?: boolean
  timer?: number
  isLoading?: boolean
  attrs?: Object
  icon?: string
  iconClass?: string
  iconSize?: number
}>(), {
  count: false,
  isVibrate: false,
  target: '_self',
  size: 'base',
  icon: '',
  isClamped: false,
  isBlur: true,
  iconClass: '',
  iconSize: 20,
  color: 'base',
  contentAlign: 'center',
  styleType: 'base',
  timer: 0,
  attrs: () => ({})
})

const timeLeft = ref(props.timer)
const interval: Ref<any> = ref(null)

useKeepAlive({
  onStart: () => {
    if (props.timer) {
      if (interval.value) clearInterval(interval.value)
      interval.value = setInterval(tick, 1000)
      timeLeft.value = props.timer
    }
  },
  onLeave: () => {
    if (interval.value) clearInterval(interval.value)
  }
})

const tick = () => {
  timeLeft.value = Math.max(0, timeLeft.value - 1)
}

const isWaiting = computed(() => {
  return timeLeft.value > 0 && props.timer > 0
})

watch(isWaiting, () => {
  if (isWaiting.value === false && interval.value) clearInterval(interval.value)
})

const gradient = computed(() => {
  if (props.color === 'base') return gradientBase
  if (props.color === 'builders') return gradientBuilders
  if (props.color === 'member') return gradientMember
  if (props.color === 'supporter') return gradientSupporter
  if (props.color === 'founder') return gradientFounder
  if (props.color === 'intentions') return gradientIntentions
  if (props.color === 'gold') return gradientGold
  if (props.color === 'spicy') return gradientSpicy

  return null
})

const BUTTON_COLORS = {
  base: 'text-white',
  builders: 'text-white',
  pioneer: 'text-white',
  supporter: 'text-white',
  founder: 'text-white',
  member: 'text-white',
  intentions: 'text-white',
  gold: 'text-white',
  emerald: 'text-white bg-emerald-500 border-2 border-emerald-500',
  whale: 'text-white bg-whale-950 border-2 border-whale-950',
  flamingo: 'text-white bg-flamingo-500 border-2 border-flamingo-500',
  butterfly: 'text-white bg-butterfly-500 border-2 border-butterfly-500',
  royal: 'text-white bg-royal-600 border-2 border-royal-600',
  slate: 'text-white bg-light-950 dark:bg-white dark:text-dark-900 dark:border-white border-2 border-light-950',
  white: 'bg-white dark:bg-dark-700 dark:text-dark-50 dark:border-dark-400/10 text-light-950 border border-charcoal-100 md:hover:border-charcoal-950 md:hover:text-white md:hover:bg-charcoal-950 ring-0 ring-charcoal-500 ring-opacity-10',
  whiteInput: 'bg-white dark:bg-dark-800 dark:text-dark-50 dark:border-dark-600 text-light-950 border border-charcoal-100 md:hover:border-charcoal-950 md:hover:text-white md:hover:bg-charcoal-950 ring-0 ring-charcoal-500 ring-opacity-10',
  whiteWeak: 'bg-charcoal-50 text-charcoal-300 border-2 border-charcoal-50 md:hover:border-charcoal-950 md:hover:text-white md:hover:bg-charcoal-950 ring-1 ring-charcoal-500 ring-opacity-10',
  weak: 'bg-charcoal-300 dark:bg-dark-500 dark:text-dark-600 text-charcoal-600 border border-transparent shadow-none md:hover:text-white md:hover:bg-charcoal-950',
  xweak: 'bg-[#d2c7cf] dark:bg-[#3f3f3f] dark:text-[#7e7e7e] text-[#9c969a] border border-transparent shadow-none md:hover:text-white md:hover:bg-charcoal-950',
  lightWhite: 'bg-none border-2 border-white md:hover:bg-white md:hover:text-light-950 text-white',
  light: 'bg-none text-light-950 border-2 border-charcoal-950 md:hover:bg-charcoal-950 md:hover:text-white dark:bg-dark-50',
  lightWeak: 'text-light-950 border-2 border-charcoal-300 md:hover:bg-charcoal-950 md:hover:text-white shadow-none dark:border-dark-300 dark:text-dark-50 dark:bg-dark-700',
  simple: 'bg-none text-light-950 border-none rounded !shadow-none !justify-start md:hover:bg-royal-50 !transform-none',
  simpleActive: 'bg-royal-50 text-light-950 border-none rounded !shadow-none !justify-start !transform-none',
  ruby: 'bg-ruby-500 text-white border-2 border-ruby-500 !shadow-none md:hover:!shadow-md md:hover:border-ruby-600 md:hover:bg-ruby-600 md:hover:text-white',
  rubyWeak: 'bg-ruby-300 text-ruby-800 border-2 border-ruby-300 !shadow-none md:hover:!shadow-md md:hover:border-ruby-300 md:hover:bg-ruby-300 md:hover:text-ruby-800',
  spicy: 'bg-ruby-900 text-white border-2 border-none !shadow-none md:hover:!shadow-md md:hover:border-ruby-600 md:hover:bg-ruby-600 md:hover:text-white',
  translucid: 'text-white bg-dark-950/80 border-none'
} as const

const styles = computed(() => defineStyles({
  container: {
    size: {
      '5xs': props.styleType == 'round' ?
        'size-4 flex items-center justify-center' : 
        'px-2 text-xs h-6 font-medium',
      '4xs': props.styleType == 'round' ?
        'size-5 flex items-center justify-center' : 
        'px-2 text-xs h-6 font-medium',
      '3xs': props.styleType == 'round' ?
        'size-6 flex items-center justify-center' : 
        'px-2 text-xs h-6 font-medium',
      '2xs': props.styleType == 'round' ?
        'size-8 flex items-center justify-center' : 
        'px-2 text-xs h-8 font-medium',

      xs: props.styleType == 'round' ?
        'size-10 flex items-center justify-center' :
        'px-2 h-10 text-sm font-medium',

      sm: props.styleType == 'round' ?
        'size-11 flex items-center justify-center' :
        'px-4 h-11 text-sm font-medium',

      base: props.styleType == 'round' ? 
        'size-12 flex items-center justify-center' :
        'px-5 h-12 text-base font-medium',

      md: props.styleType == 'round' ?
        'size-14 flex items-center justify-center' :
        'px-5 pb-2 pt-2 min-h-[56px] text-md font-medium',

      lg: props.styleType == 'round' ?
        'size-14 flex items-center justify-center' :
        'px-8 pb-3 pt-3 min-h-[50px] text-md font-medium',

      xl: props.styleType == 'round' ?
        'size-16 flex items-center justify-center' :
        'px-8 pb-3 pt-3 min-h-[50px] text-md font-medium',
    },
    isFull: ['flex justify-center w-full', 'inline-flex'],
    isLoading: [ 'pointer-events-none', '' ],
    isWaiting: [ 'pointer-events-none', '' ],
    color: BUTTON_COLORS
  },
  subtitle: {
    size: {
      '3xs': 'text-2xs',
      '2xs': 'text-2xs',
      xs: 'text-xs',
      sm: 'text-xs',
      base: 'text-xs',
      md: 'text-xs',
      lg: 'text-xs',
      xl: 'text-xs',
    }
  },
  blur: {
    size: {
      '5xs': 'blur-[2px] md:group-md:hover:blur-xs ',
      '4xs': 'blur-[2px] md:group-md:hover:blur-xs ',
      '3xs': 'blur-[2px] md:group-md:hover:blur-xs ',
      '2xs': 'blur-[2px] md:group-md:hover:blur-xs ',
      xs: 'blur-sm translate-y-1 md:group-md:hover:blur-xs ',
      sm: 'blur-sm translate-y-1 md:group-md:hover:blur-xs ',
      base: 'blur translate-y-1 md:group-md:hover:blur-sm ',
      md: 'blur translate-y-1 md:group-md:hover:blur-sm ',
      lg: 'blur translate-y-1 md:group-md:hover:blur-sm ',
      xl: 'blur translate-y-1 md:group-md:hover:blur-sm '
    }
  },
  content: {
    isLoading: [ 'scale-95 opacity-30', '' ],
    isWaiting: [ 'scale-95 opacity-30', '' ],
    contentAlign: {
      left: '!text-left justify-start grow',
    }
  }
}, { ...props, isWaiting: isWaiting.value }))
      
const component = computed(() => {
  if (props.href) return 'a'
  if (props.to) return resolveComponent('NuxtLink')

  return 'button'
})
</script>